import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useBookingFeedback } from '@/hooks/queries/useBookingFeedback';
import { useAuth } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/lib/api/error';
import { AuthService } from '@/services/auth.service';
import { authQueryKeys } from '@/hooks/queries/auth.queries';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, RefreshCw, Star } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const ratingLabels = ['Muito ruim', 'Ruim', 'Regular', 'Bom', 'Excelente'];

export default function BookingFeedback() {
  const model = useBookingFeedbackModel();
  return (
    <div className="bg-muted/30 min-h-screen">
      <Header showBack title="Avaliar atendimento" />
      <main className="container max-w-xl py-6 sm:py-10">{<FeedbackContent {...model} />}</main>
    </div>
  );
}

function FeedbackState({
  action,
  description,
  icon,
  title,
}: {
  action?: ReactNode;
  description?: string;
  icon?: ReactNode;
  title: string;
}) {
  return (
    <Card className="rounded-3xl">
      <CardContent className="flex min-h-80 flex-col items-center justify-center p-8 text-center">
        {icon ? (
          <span className="bg-primary/10 text-primary flex h-16 w-16 items-center justify-center rounded-2xl">
            {icon}
          </span>
        ) : null}
        <h2 className="text-foreground mt-5 text-xl font-bold">{title}</h2>
        {description ? <p className="text-muted-foreground mt-2 max-w-sm text-sm">{description}</p> : null}
        {action ? <div className="mt-6">{action}</div> : null}
      </CardContent>
    </Card>
  );
}

function FeedbackRating({
  setHoveredRating,
  hoveredRating,
  rating,
  setRating,
}: {
  setHoveredRating: React.Dispatch<React.SetStateAction<number>>;
  hoveredRating: number;
  rating: number;
  setRating: React.Dispatch<React.SetStateAction<number>>;
}) {
  return (
    <fieldset className="mt-8">
      <legend className="text-foreground text-sm font-semibold">Sua nota</legend>
      <div className="mt-3 flex flex-wrap gap-1 sm:gap-2" onMouseLeave={() => setHoveredRating(0)}>
        {[1, 2, 3, 4, 5].map((value) => {
          const selected = value <= (hoveredRating || rating);
          return (
            <button
              key={value}
              type="button"
              className="focus-visible:ring-primary hover:bg-muted flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:ring-2 focus-visible:outline-none"
              aria-label={`${value} ${value === 1 ? 'estrela' : 'estrelas'}: ${ratingLabels[value - 1]}`}
              aria-pressed={rating === value}
              onMouseEnter={() => setHoveredRating(value)}
              onFocus={() => setHoveredRating(value)}
              onBlur={() => setHoveredRating(0)}
              onClick={() => setRating(value)}
            >
              <Star className={`h-8 w-8 ${selected ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
            </button>
          );
        })}
      </div>
      <p className="text-muted-foreground mt-2 min-h-5 text-sm" aria-live="polite">
        {rating ? ratingLabels[rating - 1] : 'Toque nas estrelas para avaliar'}
      </p>
    </fieldset>
  );
}

function useBookingFeedbackModel() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const token = searchParams.get('token')?.trim() || null;
  const [tokenStatus, setTokenStatus] = useState<'idle' | 'authenticating' | 'authenticated' | 'error'>(
    token ? 'idle' : 'authenticated',
  );
  const canLoad = Boolean(bookingId) && (isAuthenticated || tokenStatus === 'authenticated');
  const { context, isError, isLoading, isSubmitting, refetch, submitFeedback } = useBookingFeedback(bookingId, canLoad);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comments, setComments] = useState('');
  useEffect(() => {
    if (!bookingId || !token) return;
    let active = true;

    async function authenticate() {
      setTokenStatus('authenticating');
      try {
        const response = await AuthService.signInWithBookingManagementToken({ token: token! });
        queryClient.setQueryData(authQueryKeys.profile(), response.user);
        if (active) {
          setTokenStatus('authenticated');
          navigate(`/agendamentos/${bookingId}/feedback`, { replace: true });
        }
      } catch {
        if (active) setTokenStatus('error');
      }
    }

    void authenticate();
    return () => {
      active = false;
    };
  }, [bookingId, navigate, queryClient, token]);
  useEffect(() => {
    if (!token && !isAuthLoading && !isAuthenticated) {
      navigate('/entrar', { replace: true, state: { from: `/agendamentos/${bookingId}/feedback` } });
    }
  }, [bookingId, isAuthenticated, isAuthLoading, navigate, token]);
  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Selecione uma nota para continuar');
      return;
    }

    try {
      await submitFeedback({ rating, ...(comments.trim() ? { comments: comments.trim() } : {}) });
      toast.success('Avaliação enviada. Obrigado!');
    } catch (error) {
      toast.error('Não foi possível enviar sua avaliação', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    }
  };
  const waitingForAccess = isAuthLoading || tokenStatus === 'idle' || tokenStatus === 'authenticating';
  return {
    navigate,
    tokenStatus,
    context,
    isError,
    isLoading,
    isSubmitting,
    refetch,
    rating,
    setRating,
    hoveredRating,
    setHoveredRating,
    comments,
    setComments,
    handleSubmit,
    waitingForAccess,
  };
}

function FeedbackContent({
  navigate,
  tokenStatus,
  context,
  isError,
  isLoading,
  isSubmitting,
  refetch,
  rating,
  setRating,
  hoveredRating,
  setHoveredRating,
  comments,
  setComments,
  handleSubmit,
  waitingForAccess,
}: ReturnType<typeof useBookingFeedbackModel>) {
  if (waitingForAccess)
    return <FeedbackState icon={<Loader2 className="h-7 w-7 animate-spin" />} title="Preparando sua avaliação..." />;
  if (tokenStatus === 'error')
    return (
      <FeedbackState
        title="Este link expirou"
        description="Entre na sua conta para avaliar o atendimento pelo histórico."
        action={<Button onClick={() => navigate('/entrar')}>Entrar</Button>}
      />
    );
  if (isLoading)
    return <FeedbackState icon={<Loader2 className="h-7 w-7 animate-spin" />} title="Carregando atendimento..." />;
  if (isError || !context)
    return (
      <FeedbackState
        title="Não foi possível abrir a avaliação"
        description="O atendimento pode ainda não ter sido concluído ou o link não é mais válido."
        action={
          <Button variant="outline" onClick={() => void refetch()}>
            <RefreshCw className="h-4 w-4" /> Tentar novamente
          </Button>
        }
      />
    );
  if (context.feedback)
    return (
      <FeedbackState
        icon={<CheckCircle2 className="h-8 w-8" />}
        title="Avaliação já enviada"
        description={`Você avaliou o atendimento na ${context.booking.companyName} com ${context.feedback.rating} estrelas.`}
        action={
          <Button asChild>
            <Link to="/perfil?tab=bookings">Ver meus atendimentos</Link>
          </Button>
        }
      />
    );
  return (
    <Card className="overflow-hidden rounded-3xl">
      <CardContent className="p-4 sm:p-8">
        <p className="text-primary text-sm font-semibold">Atendimento concluído</p>
        <h2 className="text-foreground mt-2 text-2xl font-bold">Como foi sua experiência?</h2>
        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
          {context.booking.companyName} · {context.booking.professionalName}
        </p>

        <FeedbackRating
          setHoveredRating={setHoveredRating}
          hoveredRating={hoveredRating}
          rating={rating}
          setRating={setRating}
        />

        <label htmlFor="feedback-comments" className="text-foreground mt-7 block text-sm font-semibold">
          Conte um pouco mais <span className="text-muted-foreground font-normal">(opcional)</span>
        </label>
        <textarea
          id="feedback-comments"
          value={comments}
          maxLength={500}
          rows={5}
          onChange={(event) => setComments(event.target.value)}
          placeholder="O que você mais gostou? Algo poderia ter sido melhor?"
          className="border-input bg-background focus-visible:ring-primary mt-2 w-full resize-none rounded-xl border p-3 text-base outline-none focus-visible:ring-2"
        />
        <p className="text-muted-foreground mt-1 text-right text-xs">{comments.length}/500</p>

        <Button className="mt-6 h-12 w-full" disabled={!rating || isSubmitting} onClick={() => void handleSubmit()}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {isSubmitting ? 'Enviando...' : 'Enviar avaliação'}
        </Button>
        <p className="text-muted-foreground mt-3 text-center text-xs">
          Sua avaliação ajuda o estabelecimento e outros clientes.
        </p>
      </CardContent>
    </Card>
  );
}
